import React, { useState, useEffect } from "react";
import { TrendingUp, Package, FileText, AlertTriangle } from "lucide-react";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import "./Dashboard.scss";

const Dashboard = ({ mantisBusinessId, token, refreshTrigger }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30d");

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mantisBusinessId, refreshTrigger, dateRange]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const fechaHasta = new Date();
      const fechaDesde = new Date();
      if (dateRange === "7d") fechaDesde.setDate(fechaDesde.getDate() - 7);
      else if (dateRange === "30d") fechaDesde.setDate(fechaDesde.getDate() - 30);
      else if (dateRange === "90d") fechaDesde.setDate(fechaDesde.getDate() - 90);

      const res = await roldanApi.getDashboardStats(mantisBusinessId, token, fechaDesde, fechaHasta);
      if (res?.success) setStats(res.data);
    } catch (error) {
      console.error("Error loading dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Cargando dashboard...</div>;
  if (!stats) return <div>Error al cargar datos</div>;

  const errorPercentage = stats.total_remesas > 0
    ? ((stats.remesas_con_errores / stats.total_remesas) * 100).toFixed(1)
    : 0;

  return (
    <div className="roldan-dashboard-view">
      <div className="dashboard-header">
        <h1>Dashboard Operacional</h1>
        <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
          <option value="7d">Últimos 7 días</option>
          <option value="30d">Últimos 30 días</option>
          <option value="90d">Últimos 90 días</option>
        </select>
      </div>

      <div className="kpi-cards">
        <div className="kpi-card">
          <Package size={32} />
          <div>
            <h3>Total Remesas</h3>
            <p className="kpi-value">{stats.total_remesas || 0}</p>
          </div>
        </div>
        <div className="kpi-card">
          <FileText size={32} />
          <div>
            <h3>Total Manifiestos</h3>
            <p className="kpi-value">{stats.total_manifiestos || 0}</p>
          </div>
        </div>
        <div className="kpi-card error">
          <AlertTriangle size={32} />
          <div>
            <h3>% Remesas con Errores</h3>
            <p className="kpi-value">{errorPercentage}%</p>
          </div>
        </div>
        <div className="kpi-card">
          <TrendingUp size={32} />
          <div>
            <h3>Tiempo Promedio</h3>
            <p className="kpi-value">{stats.tiempo_promedio_horas ? stats.tiempo_promedio_horas.toFixed(1) : 0}h</p>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h2>Evolución de Manifiestos</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats.manifiestosPorDia || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fecha" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="cantidad" stroke="#3b82f6" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h2>Top 5 Clientes</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.topClientes || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="cliente" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="cantidad_remesas" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h2>Top 5 Destinos</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={stats.topDestinos || []}
                dataKey="cantidad_remesas"
                nameKey="direccion_destinatario"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {(stats.topDestinos || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"][index % 5]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {stats.rankingVehiculos && stats.rankingVehiculos.length > 0 && (
        <div className="ranking-table">
          <h2>Ranking de Vehículos</h2>
          <table>
            <thead>
              <tr>
                <th>Placa</th>
                <th>Tipo</th>
                <th>Viajes</th>
                <th>Remesas</th>
              </tr>
            </thead>
            <tbody>
              {stats.rankingVehiculos.map((v, idx) => (
                <tr key={idx}>
                  <td>{v.placa_vehiculo || "N/A"}</td>
                  <td>{v.tipo_vehiculo || "N/A"}</td>
                  <td>{v.cantidad_viajes || 0}</td>
                  <td>{v.cantidad_remesas || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

